import SportsActivityCard from "@/components/dashboard/sport-activity.tsx";
import {
  UserStatCard,
  UserStatCardSkeleton,
} from "@/components/dashboard/user-stat-card";
import { FriendsDialog } from "@/components/friends/friends-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useRecentActivities } from "@/data/activities/hooks";
import { useCurrentUser } from "@/data/auth/hooks";
import { useFriends } from "@/data/users/hooks";
import { getFallbackName } from "@/lib/name-utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import confetti from "canvas-confetti";
import { Plus } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/profiles/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { data: friendsData, isLoading: isLoadingFriends } = useFriends();
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(null);

  // Fetch activities for the selected user or all activities if none selected
  const { data: activities, isLoading: isLoadingActivities } =
    useRecentActivities(selectedFriendId || currentUser?.id || "");

  // Function to trigger confetti effect when claiming an activity
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });
  };

  // Only block rendering if we don't have the current user yet
  if (!currentUser && isLoadingUser) {
    return (
      <div className="mx-auto p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
        <ProfilePageSkeleton />
      </div>
    );
  }

  if (!currentUser) {
    return <div>Failed to load user data. Please refresh the page.</div>;
  }

  return (
    <div className="mx-auto p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
      {/* Friends row at the top */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 w-full">
        <div className="flex overflow-x-auto gap-4 w-full p-2">
          {/* Current user avatar */}
          {!isLoadingUser && currentUser && (
            <div
              className={`flex flex-col items-center cursor-pointer transition-all ${selectedFriendId === currentUser.id ? "scale-110" : "opacity-80 hover:opacity-100"}`}
              onClick={() => setSelectedFriendId(currentUser.id)}
            >
              <Avatar
                className={`h-16 w-16 mb-1 transition-all ${selectedFriendId === currentUser.id ? "ring-2 ring-blue-400" : ""}`}
              >
                <AvatarImage src={currentUser.avatarUrl} />
                <AvatarFallback>
                  {getFallbackName(currentUser.firstName, currentUser.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium truncate max-w-16 text-center">
                You
              </span>
            </div>
          )}

          {/* Friends avatars */}
          {isLoadingFriends
            ? // Skeleton loaders for friends
              Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex flex-col items-center">
                  <Skeleton className="h-16 w-16 rounded-full mb-1" />
                  <Skeleton className="h-4 w-12" />
                </div>
              ))
            : friendsData?.friends.map((friend) => (
                <div
                  key={friend.id}
                  className={`flex flex-col items-center cursor-pointer transition-all ${selectedFriendId === friend.id ? "scale-110" : "opacity-80 hover:opacity-100"}`}
                  onClick={() => setSelectedFriendId(friend.id)}
                >
                  <Avatar
                    className={`h-16 w-16 mb-1 transition-all ${selectedFriendId === friend.id ? "ring-2 ring-blue-400" : ""}`}
                  >
                    <AvatarImage src={friend.avatarUrl} />
                    <AvatarFallback>
                      {getFallbackName(friend.firstName, friend.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-medium truncate max-w-16 text-center">
                    {friend.firstName}
                  </span>
                </div>
              ))}

          {/* Add friend button */}
          <FriendsDialog>
            <div className="flex flex-col items-center cursor-pointer opacity-80 hover:opacity-100 transition-all">
              <div className="h-16 w-16 mb-1 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <Plus className="h-8 w-8 text-gray-500" />
              </div>
              <span className="text-xs font-medium truncate max-w-16 text-center">
                Add Friend
              </span>
            </div>
          </FriendsDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-220px)]">
        {/* Left column - User profile */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            {/* User profile card with skeleton while loading */}
            {isLoadingActivities ? (
              <UserStatCardSkeleton />
            ) : (
              <UserStatCard
                userId={selectedFriendId || currentUser?.id || ""}
              />
            )}
          </div>

          {/* Only show customize button for current user's profile */}
          {(!selectedFriendId || selectedFriendId === currentUser?.id) && (
            <Button
              className="w-full mt-2"
              variant="outline"
              onClick={() => navigate({ to: "/profiles/customize" })}
            >
              <p className="bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
                Customize your profile
              </p>
            </Button>
          )}
        </div>

        {/* Right column - Activities feed */}
        <div className="lg:col-span-3 lg:overflow-y-auto lg:pr-2 lg:h-full">
          <div className="space-y-4">
            {isLoadingActivities ? (
              // Activity skeletons
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-sm shadow-sm border p-3 lg:p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              ))
            ) : activities && activities.length > 0 ? (
              activities.map((activity) => (
                <SportsActivityCard
                  key={activity.id}
                  activity={activity}
                  triggerConfetti={triggerConfetti}
                />
              ))
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">No activities found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Skeleton component for the entire profile page
function ProfilePageSkeleton() {
  return (
    <>
      {/* Friends row skeleton */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 w-full">
        <div className="flex overflow-x-auto gap-4 w-full p-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex flex-col items-center">
              <Skeleton className="h-16 w-16 rounded-full mb-1" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-220px)]">
        {/* Left column skeleton */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4 mb-2">
            <UserStatCardSkeleton />
          </div>
          <Skeleton className="h-10 w-full mt-2" />
        </div>

        {/* Right column skeleton */}
        <div className="lg:col-span-3 lg:overflow-y-auto lg:pr-2 lg:h-full">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-sm shadow-sm border p-3 lg:p-4 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
